/**
 * ShellLayout — AppLayout과 AgentLayout이 공유하는 단일 셸.
 *
 * Rail + 선택적 사이드 패널 + 선택적 헤더 + 메인 콘텐츠.
 * 레이아웃별 차이는 slot props로 주입.
 */
import { LayoutHeader } from "@/shared/components";
import { AgentRail } from "@/shared/layouts/agent-rail";
import { RAIL_MAIN, RAIL_BOTTOM } from "@/shared/layouts/rail-items";
import { cn } from "@/shared/lib/utils";

export interface ShellLayoutProps {
  /** 사이드 패널 (SidePanel 컴포넌트 등). null이면 미표시. */
  sidePanel?: React.ReactNode;
  /** LayoutHeader children으로 전달될 헤더 콘텐츠 */
  header?: React.ReactNode;
  /** false면 헤더 미렌더 (ChatZone이 자체 ChatHeader 사용) */
  renderHeader?: boolean;
  /** 메인 콘텐츠 */
  children: React.ReactNode;
  /** 모바일 내비게이션 (MobileTabBar 등). 최하단 고정. */
  mobileNav?: React.ReactNode;
  /** 오버레이 (MobileNavSheet 등). 레이아웃 외부 렌더. */
  overlays?: React.ReactNode;
  /** Rail 활성 아이템 ID */
  railActiveItem?: string;
  /** Rail 아이템 클릭 핸들러 */
  onRailItemClick?: (id: string) => void;
  className?: string;
}

export function ShellLayout({
  sidePanel,
  header,
  renderHeader = true,
  children,
  mobileNav,
  overlays,
  railActiveItem,
  onRailItemClick,
  className,
}: ShellLayoutProps) {
  return (
    <>
      <div
        className={cn(
          "flex h-screen overflow-hidden bg-surface-base",
          className,
        )}
      >
        {/* Zone 1: Rail (자체 hidden sm:flex) */}
        <AgentRail
          main={RAIL_MAIN}
          bottom={RAIL_BOTTOM}
          activeItem={railActiveItem}
          onItemClick={onRailItemClick}
        />

        {/* Zone 2: 사이드 패널 (선택적) */}
        {sidePanel}

        {/* Zone 3: 메인 영역 */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {renderHeader && header && <LayoutHeader>{header}</LayoutHeader>}
          <div className="flex flex-1 overflow-hidden">{children}</div>
        </div>

        {/* 모바일 내비 (fixed bottom 등) */}
        {mobileNav}
      </div>

      {/* 오버레이 (Sheet, Portal 등 — flex 컨테이너 밖) */}
      {overlays}
    </>
  );
}
